import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  Server, Users, DollarSign, Activity, Zap, AlertTriangle, Building2,
  ShieldAlert, Plug, BarChart3, Share2, Settings as SettingsIcon, LayoutDashboard,
} from "lucide-react";
import { AnimatedNumber } from "../components/AnimatedNumber";
import AdminLayout from "./admin/AdminLayout";

function Stat({ icon: Icon, label, value, prefix = "", color = "text-ai" }) {
  return (
    <div className="surface p-5">
      <div className={`h-9 w-9 rounded-xl grid place-items-center bg-zinc-50 ${color} mb-3 border border-line`}><Icon className="h-4 w-4" /></div>
      <p className="text-[11px] uppercase tracking-wider text-meta font-semibold">{label}</p>
      <p className="mt-1 font-display text-3xl font-extrabold text-ink"><AnimatedNumber value={value} prefix={prefix} /></p>
    </div>
  );
}

const SECTION_META = {
  overview: { icon: LayoutDashboard, title: "Overview", subtitle: "Platform health at a glance" },
  users: { icon: Users, title: "Users", subtitle: "Accounts across the platform" },
  workspaces: { icon: Building2, title: "Workspaces", subtitle: "Every business unit on Ashra" },
  billing: { icon: DollarSign, title: "Billing", subtitle: "Payments · subscriptions · cost" },
  amplify: { icon: Share2, title: "Amplify", subtitle: "Affiliate · referral economics" },
  moderation: { icon: ShieldAlert, title: "Moderation", subtitle: "Risk · suspicious activity" },
  integrations: { icon: Plug, title: "Integrations", subtitle: "Provider connections" },
  insights: { icon: BarChart3, title: "Insights", subtitle: "AI usage · platform metrics" },
  infrastructure: { icon: Server, title: "Infrastructure", subtitle: "Queues · cache · webhooks" },
  settings: { icon: SettingsIcon, title: "Settings", subtitle: "Platform configuration" },
};

export default function Admin() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [payments, setPayments] = useState([]);
  const [usage, setUsage] = useState({ daily: [], monthly: [] });
  const [system, setSystem] = useState(null);
  const [params, setParams] = useSearchParams();
  const section = params.get("section") || "overview";
  const setSection = (id) => { params.set("section", id); setParams(params, { replace: true }); };

  useEffect(() => {
    if (!user?.is_admin) return;
    (async () => {
      try {
        const [o, w, p, u, s] = await Promise.all([
          api.get("/admin/overview"),
          api.get("/admin/workspaces"),
          api.get("/admin/payments"),
          api.get("/admin/ai-usage"),
          api.get("/admin/system"),
        ]);
        setOverview(o.data); setWorkspaces(w.data); setPayments(p.data); setUsage(u.data); setSystem(s.data);
      } catch {}
    })();
  }, [user]);

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const meta = SECTION_META[section] || SECTION_META.overview;
  const Icon = meta.icon;

  return (
    <AdminLayout active={section} onChange={setSection}>
      <div className="p-6 lg:p-10 max-w-[1600px]" data-testid={`admin-section-${section}`}>
        <div className="flex items-center gap-3 mb-6">
          <Icon className="h-6 w-6 text-amber-600" />
          <div>
            <h1 className="font-display text-3xl font-bold text-ink">{meta.title}</h1>
            <p className="text-sm text-meta">{meta.subtitle}</p>
          </div>
        </div>

        {section === "overview" && overview && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Stat icon={Users} label="Workspaces" value={overview.workspaces} color="text-ai" />
              <Stat icon={Users} label="Users" value={overview.users} color="text-info" />
              <Stat icon={DollarSign} label="Platform revenue" value={Math.round(overview.platform_revenue_usd)} prefix="$" color="text-success" />
              <Stat icon={Zap} label="AI calls today" value={overview.ai_calls_today} color="text-warning" />
            </div>
            {system && (
              <div className="surface p-6 mb-4">
                <h3 className="font-display text-lg font-bold text-ink mb-3 inline-flex items-center gap-2"><Activity className="h-4 w-4 text-success" /> System health</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 text-sm">
                  <KV label="Status" value={<span className="text-success font-bold capitalize">{system.system_health}</span>} />
                  <KV label="Pending payments" value={system.pending_payments} />
                  <KV label="Queue failures 24h" value={system.queue_failures_24h} />
                  <KV label="Webhook failures 24h" value={system.webhook_failures_24h} />
                  <KV label="Suspicious workspaces" value={system.suspicious_workspaces} />
                </div>
              </div>
            )}
            <div className="rounded-3xl border border-amber-200 bg-warning-soft p-5 text-warning text-sm inline-flex gap-2 font-medium">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              Live fraud detection, IP velocity tracking, and Meta App reputation safeguards arrive in v2.
            </div>
          </>
        )}

        {section === "users" && (
          <div className="surface p-6">
            <p className="text-sm text-meta">Showing {workspaces.reduce((a, w) => a + (w.user_count || 0), 0)} users across {workspaces.length} workspaces.</p>
            <Table headers={["Workspace", "Users", "Plan", "Created"]} rows={workspaces.map(w => [w.name, w.user_count, w.plan, w.created_at?.slice(0,10)])} />
          </div>
        )}

        {section === "workspaces" && (
          <div className="surface p-6">
            <Table headers={["Workspace", "Plan", "Users", "Leads", "Created"]} rows={workspaces.map(w => [w.name, w.plan, w.user_count, w.lead_count, w.created_at?.slice(0,10)])} />
          </div>
        )}

        {section === "billing" && (
          <div className="surface p-6">
            <h3 className="font-display text-lg font-bold text-ink mb-3">Recent payments</h3>
            <Table headers={["Provider", "Amount", "Status", "When"]} rows={payments.map(p => [p.provider || "stripe", `$${p.amount} ${p.currency?.toUpperCase()}`, p.payment_status, p.created_at?.slice(0,16).replace("T"," ")])} />
          </div>
        )}

        {section === "amplify" && (
          <div className="surface p-6">
            <p className="text-sm text-meta">Affiliate & referral revenue across all workspaces will land here as Amplify usage grows. For now, configure commissions inside each workspace under <span className="font-mono">/amplify</span>.</p>
          </div>
        )}

        {section === "moderation" && system && (
          <div className="surface p-6 space-y-3">
            <KV label="Suspicious workspaces" value={system.suspicious_workspaces} />
            <KV label="Queue failures 24h" value={system.queue_failures_24h} />
            <KV label="Webhook failures 24h" value={system.webhook_failures_24h} />
            <p className="text-xs text-meta">Granular moderation tooling — flagged conversations, rate-limit offenders, content policy — ships in v2.</p>
          </div>
        )}

        {section === "integrations" && (
          <div className="surface p-6">
            <p className="text-sm text-meta">Stripe (sandbox), Razorpay, Paddle, Meta IG/WhatsApp — provider health, key rotation, and webhook delivery diagnostics arrive in v2.</p>
          </div>
        )}

        {section === "insights" && (
          <div className="surface p-6">
            <h3 className="font-display text-lg font-bold text-ink mb-3">AI usage today</h3>
            <Table headers={["Workspace", "Calls", "Cost"]} rows={usage.daily.map(u => [u.workspace_id?.slice(0,16) + "…", u.calls, `$${(u.cost || 0).toFixed(4)}`])} empty="No AI usage today." />
          </div>
        )}

        {section === "infrastructure" && system && (
          <div className="surface p-6 space-y-3">
            <KV label="System health" value={<span className="text-success font-bold capitalize">{system.system_health}</span>} />
            <KV label="Pending payments" value={system.pending_payments} />
            <KV label="Queue failures 24h" value={system.queue_failures_24h} />
            <KV label="Webhook failures 24h" value={system.webhook_failures_24h} />
          </div>
        )}

        {section === "settings" && (
          <div className="surface p-6">
            <p className="text-sm text-meta">Platform-wide feature flags, default plans, and onboarding policies — admin controls arrive in v2. Per-workspace settings remain inside the Creator OS Settings section.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-meta">{label}</span>
      <span className="text-ink font-semibold">{value}</span>
    </div>
  );
}

function Table({ headers, rows, empty }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-meta uppercase tracking-wider font-semibold">
          <tr>{headers.map(h => <th key={h} className="py-2 pr-3">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="py-4 text-meta">{empty || "No data."}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j} className="py-2 pr-3 text-ink-soft">{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
